import { useEffect, useState, type RefObject } from "react";

/** Only the game hides navigation. Upward scrolling, keyboard focus and a visible
 * restore button always recover it; modal interactions pin navigation in place. */
export function useAutoHideHeader(active: boolean, header: RefObject<HTMLElement | null>) {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    setHidden(false);
    if (!active) return;
    let previous = Math.max(0, window.scrollY);
    let distance = 0;
    let direction = 0;
    function reveal() { setHidden(false); distance = 0; previous = Math.max(0, window.scrollY); }
    function onScroll() {
      const y = Math.max(0, window.scrollY), delta = y - previous;
      previous = y;
      if (y < 100 || document.querySelector('dialog[open]') || header.current?.querySelector(':focus-visible')) { reveal(); return; }
      const next = Math.sign(delta);
      if (next !== direction) { distance = 0; direction = next; }
      distance += Math.abs(delta);
      if (next < 0 && distance > 8) setHidden(false);
      if (next > 0 && distance > 28) setHidden(true);
    }
    function onKey(event: KeyboardEvent) { if (event.key === "Tab" || event.key === "Escape") reveal(); }
    function onFocus(event: FocusEvent) {
      const target = event.target as HTMLElement;
      if (header.current?.contains(target) || target.closest?.('dialog[open],[role="dialog"]')) reveal();
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("keydown", onKey);
    window.addEventListener("focusin", onFocus);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("keydown", onKey); window.removeEventListener("focusin", onFocus); };
  }, [active, header]);
  return { hidden: active && hidden, reveal: () => setHidden(false) };
}
